import { CalendarPlus } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { PublicEvent } from "@classview/shared-types";

type AddEventToCalendarModalProps = {
  event: PublicEvent;
};

export const AddEventToCalendarModal = ({
  event,
}: AddEventToCalendarModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  const getGoogleLink = () => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    return `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURIComponent(event.session.name)}&dates=${formatDate(event.session.start)}/${formatDate(event.session.end)}&details=${encodeURIComponent(event.session.description ?? "")}`;
  };

  const getOutlookLink = () => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().split(".")[0];
    };

    return `https://outlook.live.com/calendar/0/deeplink/compose?subject=${encodeURIComponent(event.session.name)}&body=${encodeURIComponent(event.session.description ?? "")}&startdt=${formatDate(event.session.start)}&enddt=${formatDate(event.session.end)}`;
  };

  const getICalLink = () => {
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
    };

    const icalData = [
      "BEGIN:VCALENDAR",
      "VERSION:2.0",
      "BEGIN:VEVENT",
      `DTSTART:${formatDate(event.session.start)}`,
      `DTEND:${formatDate(event.session.end)}`,
      `SUMMARY:${event.session.name}`,
      `DESCRIPTION:${event.session.description ?? ""}`,
      "END:VEVENT",
      "END:VCALENDAR",
    ].join("\n");

    return `data:text/calendar;charset=utf8,${encodeURIComponent(icalData)}`;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="link" size="xs" className="p-0">
          Add event to Calendar
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add event to Calendar</DialogTitle>
          <DialogDescription>Add this event to your calendar</DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-muted-foreground">
              On registration, we sent you an email that should've added an
              event to your calendar. You can also click on one of the buttons
              to manually add the event to your calendar.
            </p>
            <Link
              to={getGoogleLink()}
              className="w-full"
              target="_blank"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <Button
                variant="outline"
                className="flex w-full items-center gap-2"
              >
                <img src="/google.svg" alt="Google Calendar" />
                Google Calendar
              </Button>
            </Link>
            <Link
              to={getOutlookLink()}
              className="w-full"
              target="_blank"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <Button
                variant="outline"
                className="flex w-full items-center gap-2"
              >
                <img src="/microsoft.svg" alt="Outlook Calendar" />
                Outlook
              </Button>
            </Link>
            <Link
              to={getICalLink()}
              className="w-full"
              download="event.ics"
              onClick={() => {
                setIsOpen(false);
              }}
            >
              <Button
                variant="outline"
                className="flex w-full items-center gap-2"
              >
                <CalendarPlus size={16} /> iCal (Apple / Outlook)
              </Button>
            </Link>
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};
